import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { PageHeader } from "../../../components/Page";
import Hamburger from "../../../components/Hamburger";
import { ReactComponent as GNAiIcon } from "../../../icons/gnai_logo.svg";
import {
  deriveAnalytics,
  deriveReturnAnalytics,
  GRANULARITY_TO_GROUP_BY,
} from "./helpers";
import { DetailTab, Granularity, TopTab } from "./types";
import { ContractsSidebar, CUMULATIVE_ID } from "./components/ContractsSidebar";
import { DetailTitle } from "./components/DetailTitle";
import { DetailTabs } from "./components/DetailTabs";
import { OverviewTab } from "./tabs/OverviewTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { CumulativeView } from "./tabs/CumulativeView";
import {
  getBankingCumulativeOverview,
  getBankingCumulativeReturn,
  getBankingCumulativeSupply,
  getBankingCumulativeSupplyDestinations,
  getBankingCumulativeSupplySources,
  getBankingContractAnalytics,
  getBankingContractReturnAnalytics,
} from "../../../Rest_api/RestApi";
import {
  BankingContractAnalytics,
  BankingContractAnalyticsBucket,
  BankingContractReturnAnalytics,
  BankingCumulativeOverview,
  BankingCumulativeReturnAnalytics,
  BankingCumulativeSupplyAnalytics,
  BankingCumulativeSupplyDestinations,
  BankingCumulativeSupplySources,
} from "../../../models/banking";

function bucketToRow(d: BankingContractAnalyticsBucket, isBlock: boolean) {
  return isBlock
    ? {
        Date: d.date,
        "Time Block": d.time_block ?? "",
        Time: d.time ?? "",
        "Scheduled MWh": +d.scheduled_mwh.toFixed(2),
        "Actual MWh": +d.actual_mwh.toFixed(2),
        "Delta MWh": +d.delta_mwh.toFixed(2),
        "Delta %": +d.delta_pct.toFixed(1),
      }
    : {
        Date: d.date,
        "Scheduled MWh": +d.scheduled_mwh.toFixed(2),
        "Actual MWh": +d.actual_mwh.toFixed(2),
        "Delta MWh": +d.delta_mwh.toFixed(2),
        "Delta %": +d.delta_pct.toFixed(1),
      };
}

function downloadWorkbook(wb: XLSX.WorkBook, filename: string) {
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], { type: "application/octet-stream" }),
    filename.replace(/[^a-zA-Z0-9_\-.]/g, "_"),
  );
}

export function BankingCalculator() {
  const [topTab, setTopTab] = useState<TopTab>("Active");
  const [detailTab, setDetailTab] = useState<DetailTab>("Overview");
  const [granularity, setGranularity] = useState<Granularity>("Day");
  const [selectedId, setSelectedId] = useState<string>(CUMULATIVE_ID);
  const [search, setSearch] = useState("");

  const [analytics, setAnalytics] = useState<BankingContractAnalytics | null>(
    null,
  );
  const [returnAnalytics, setReturnAnalytics] =
    useState<BankingContractReturnAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const [cumulativeOverview, setCumulativeOverview] =
    useState<BankingCumulativeOverview | null>(null);
  const [cumulativeSupply, setCumulativeSupply] =
    useState<BankingCumulativeSupplyAnalytics | null>(null);
  const [cumulativeReturn, setCumulativeReturn] =
    useState<BankingCumulativeReturnAnalytics | null>(null);
  const [cumulativeSources, setCumulativeSources] =
    useState<BankingCumulativeSupplySources | null>(null);
  const [cumulativeDestinations, setCumulativeDestinations] =
    useState<BankingCumulativeSupplyDestinations | null>(null);
  const [cumulativeLoading, setCumulativeLoading] = useState(false);
  const [cumulativeError, setCumulativeError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);

  const isContractSelected =
    selectedId !== "" && selectedId !== CUMULATIVE_ID;

  useEffect(() => {
    if (!isContractSelected) {
      setAnalytics(null);
      setReturnAnalytics(null);
      setAnalyticsError(null);
      setAnalyticsLoading(false);
      return;
    }
    let cancelled = false;
    setAnalytics(null);
    setReturnAnalytics(null);
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    Promise.all([
      getBankingContractAnalytics(
        selectedId,
        GRANULARITY_TO_GROUP_BY[granularity],
      ),
      getBankingContractReturnAnalytics(
        selectedId,
        GRANULARITY_TO_GROUP_BY[granularity],
      ).catch(() => null),
    ])
      .then(([supply, ret]) => {
        if (cancelled) return;
        if (!supply) {
          setAnalytics(null);
          setReturnAnalytics(null);
          setAnalyticsError("No analytics available for this contract.");
          return;
        }
        setAnalytics(supply);
        setReturnAnalytics(ret);
      })
      .catch((err) => {
        if (cancelled) return;
        setAnalyticsError(err?.message ?? "Failed to load analytics");
        setAnalytics(null);
        setReturnAnalytics(null);
      })
      .finally(() => {
        if (!cancelled) setAnalyticsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, granularity, isContractSelected]);

  useEffect(() => {
    if (selectedId !== CUMULATIVE_ID) {
      setCumulativeOverview(null);
      setCumulativeSupply(null);
      setCumulativeReturn(null);
      setCumulativeSources(null);
      setCumulativeDestinations(null);
      setCumulativeError(null);
      setCumulativeLoading(false);
      return;
    }
    let cancelled = false;
    setCumulativeOverview(null);
    setCumulativeSupply(null);
    setCumulativeReturn(null);
    setCumulativeSources(null);
    setCumulativeDestinations(null);
    setCumulativeLoading(true);
    setCumulativeError(null);
    Promise.all([
      getBankingCumulativeOverview().catch(() => null),
      getBankingCumulativeSupply(GRANULARITY_TO_GROUP_BY[granularity]),
      getBankingCumulativeReturn(GRANULARITY_TO_GROUP_BY[granularity]).catch(() => null),
      getBankingCumulativeSupplySources().catch(() => null),
      getBankingCumulativeSupplyDestinations().catch(() => null),
    ])
      .then(([overview, supply, ret, sources, destinations]) => {
        if (cancelled) return;
        if (!supply) {
          setCumulativeError("No cumulative data available.");
          return;
        }
        setCumulativeOverview(overview);
        setCumulativeSupply(supply);
        setCumulativeReturn(ret);
        setCumulativeSources(sources);
        setCumulativeDestinations(destinations);
      })
      .catch((err) => {
        if (cancelled) return;
        setCumulativeError(err?.message ?? "Failed to load cumulative analytics");
        setCumulativeSupply(null);
        setCumulativeReturn(null);
      })
      .finally(() => {
        if (!cancelled) setCumulativeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, granularity]);

  useEffect(() => {
    if (selectedId === CUMULATIVE_ID) setGranularity("Time Block");
  }, [selectedId]);

  const derived = useMemo(
    () => (analytics ? deriveAnalytics(analytics, granularity) : null),
    [analytics, granularity],
  );

  const returnDerived = useMemo(
    () =>
      returnAnalytics ? deriveReturnAnalytics(returnAnalytics, granularity) : null,
    [returnAnalytics, granularity],
  );

  // Export for individual contract — uses already-loaded data for current granularity
  const handleExport = useCallback(() => {
    if (!analytics) return;
    const isBlock = granularity === "Time Block";
    const wb = XLSX.utils.book_new();

    // Supply sheet for current granularity
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(analytics.daily.map((d) => bucketToRow(d, isBlock))),
      `Supply (${granularity})`,
    );

    // Return sheet for current granularity
    if (returnAnalytics && returnAnalytics.daily.length > 0) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(returnAnalytics.daily.map((d) => bucketToRow(d, isBlock))),
        `Return (${granularity})`,
      );
    }

    downloadWorkbook(
      wb,
      `banking_${analytics.contract.supplier}_${analytics.contract.receiver}_${granularity}.xlsx`,
    );
  }, [analytics, returnAnalytics, granularity]);

  // Export for cumulative view — uses already-loaded cumulative data
  const handleCumulativeExport = useCallback(() => {
    if (!cumulativeSupply) return;
    setExporting(true);
    try {
      const isBlock = granularity === "Time Block";
      const wb = XLSX.utils.book_new();

      // Supply sheet
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          cumulativeSupply.daily.map((d) => bucketToRow(d, isBlock)),
        ),
        `Supply (${granularity})`,
      );

      // Return sheet
      if (cumulativeReturn && cumulativeReturn.daily.length > 0) {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(
            cumulativeReturn.daily.map((d) => bucketToRow(d, isBlock)),
          ),
          `Return (${granularity})`,
        );
      }

      downloadWorkbook(wb, `banking_cumulative_${granularity}.xlsx`);
    } finally {
      setExporting(false);
    }
  }, [cumulativeSupply, cumulativeReturn, cumulativeOverview, granularity]);

  const leftHeader = (
    <div className="flex items-center gap-5">
      <GNAiIcon width={80} />
      <Hamburger />
      <h1 className="text-[18px] text-[#6F6D7C]">Banking Calculator</h1>
    </div>
  );

  return (
    <div className="page-wrapper">
      <PageHeader leftComponent={leftHeader} />

      <div className="tabContent">
        <div className="flex flex-row w-full bg-[#F7F8FB] min-h-[calc(100vh-80px)] mt-3">
          <ContractsSidebar
            topTab={topTab}
            setTopTab={setTopTab}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            search={search}
            setSearch={setSearch}
          />

          <section className="flex-1 px-3 pb-8 overflow-y-auto">
            {selectedId === CUMULATIVE_ID && cumulativeLoading && !cumulativeSupply && (
              <div className="bg-white rounded-md p-6 text-[13px] text-[#6B6877]">
                Loading cumulative data…
              </div>
            )}

            {selectedId === CUMULATIVE_ID && cumulativeError && !cumulativeLoading && (
              <div className="bg-white rounded-md p-6 text-[13px] text-[#E0463C]">
                {cumulativeError}
              </div>
            )}

            {selectedId === CUMULATIVE_ID && cumulativeSupply && (
              <>
                <div className="flex flex-col gap-1.5 bg-white p-5 rounded-s-md rounded-e-md">
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="text-[20px] font-bold text-[#525062]">
                      All contracts
                    </h1>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleCumulativeExport}
                        disabled={exporting}
                        className="inline-flex items-center gap-1.5 border border-[#ECEEF3] bg-white rounded-lg px-3 py-1.5 text-xs text-[#525062] font-medium hover:border-[#DADCE3] hover:bg-[#FAFBFD] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Export
                      </button>
                    </div>
                  </div>
                  {cumulativeOverview && (
                    <div className="text-xs text-[#6B6877] flex flex-wrap items-center gap-1">
                      {[
                        { label: "Contracts combined", value: String(cumulativeOverview.contracts_combined) },
                        { label: "Avg banking factor", value: `${cumulativeOverview.avg_banking_factor_pct.toFixed(1)}%` },
                        { label: "Drawing supply", value: String(cumulativeOverview.drawing_supply) },
                        { label: "Returning power", value: String(cumulativeOverview.returning_power) },
                        { label: "Giving supply", value: String(cumulativeOverview.giving_supply) },
                        { label: "Receiving back", value: String(cumulativeOverview.receiving_back) },
                        {
                          label: "Net variance",
                          value: `${cumulativeOverview.net_variance_pct >= 0 ? "+" : ""}${cumulativeOverview.net_variance_pct.toFixed(2)}%`,
                          neg: cumulativeOverview.net_variance_pct < 0,
                        },
                      ].map((item, i, arr) => (
                        <Fragment key={item.label}>
                          <span className="flex gap-2">
                            {item.label}{" "}
                            <strong
                              className={
                                "neg" in item && item.neg
                                  ? "text-[#E0463C] font-medium"
                                  : "text-[#4A5160] font-medium"
                              }
                            >
                              {item.value}
                            </strong>
                          </span>
                          {i < arr.length - 1 && (
                            <span className="text-[#828A99] mx-1">·</span>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  )}
                </div>
                <CumulativeView
                  supplyAnalytics={cumulativeSupply}
                  returnAnalytics={cumulativeReturn}
                  supplySources={cumulativeSources}
                  supplyDestinations={cumulativeDestinations}
                  granularity={granularity}
                  setGranularity={setGranularity}
                />
              </>
            )}

            {isContractSelected && analyticsLoading && !derived && (
              <div className="bg-white rounded-md p-6 text-[13px] text-[#6B6877]">
                Loading analytics…
              </div>
            )}

            {isContractSelected && analyticsError && !analyticsLoading && (
              <div className="bg-white rounded-md p-6 text-[13px] text-[#E0463C]">
                {analyticsError}
              </div>
            )}

            {isContractSelected && derived && (
              <>
                <DetailTitle
                  contract={derived.contract}
                  onExport={handleExport}
                  exporting={exporting}
                />
                <DetailTabs
                  detailTab={detailTab}
                  setDetailTab={setDetailTab}
                />

                {detailTab === "Overview" && (
                  <OverviewTab
                    contract={derived.contract}
                    totals={derived.totals}
                    returnRequired={derived.returnRequired}
                    avgReturn={derived.avgReturn}
                    series={derived.series}
                    granularity={granularity}
                    setGranularity={setGranularity}
                    rows={derived.rows}
                    returnDerived={returnDerived}
                  />
                )}
                {detailTab === "Documents" && <DocumentsTab contractId={selectedId} />}
              </>
            )}

            {!isContractSelected && selectedId !== CUMULATIVE_ID && (
              <div className="bg-white rounded-md p-6 text-[13px] text-[#6B6877]">
                Select a contract from the sidebar to view its analytics.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default BankingCalculator;
